import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { AddressViewModel } from '../../../models/Entities/Person/Address/AddressModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonAddressFormComponent from './PersonAddress.Form.Component';

// functions
import * as ComponentFunctions from './PersonAddresses.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonAddressesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetAddressesByPersonId(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedAddressId(undefined);
    openForm();
  };

  const handleEdit = (addressId: number) => {
    setSelectedAddressId(addressId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedAddressId(undefined);
    dismissForm();
  };

  const Controls = (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.addresses.list.add', { defaultValue: 'Agregar domicilio' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />
    </>
  );

  return (
    <>
      {Controls}

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.addresses.list.error', { defaultValue: 'No fue posible obtener los domicilios' })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.addresses.length === 0 && (
        <MessageComponent
          message={t('persons.addresses.list.empty', { defaultValue: 'No hay domicilios registrados' })}
          type={MessageBarType.info}
        />
      )}

      {props.addresses.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.addresses.list.street', { defaultValue: 'Calle y número' })}</th>
              <th>{t('persons.addresses.list.city', { defaultValue: 'Ciudad' })}</th>
              <th>{t('persons.addresses.list.postalCode', { defaultValue: 'Código postal' })}</th>
              <th>{t('persons.addresses.list.country', { defaultValue: 'País' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.addresses.map((address: AddressViewModel) => (
              <tr key={address.id} className="TableBodyRow">
                <td className="primary">
                  <Label>{address.street || '-'}</Label>
                </td>
                <td>
                  <Text>{address.city || '-'}</Text>
                </td>
                <td>
                  <Text>{address.postalCode || '-'}</Text>
                </td>
                <td>
                  <Text>{address.country || '-'}</Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(address.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonAddressDeleteButton-${address.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.addresses.list.deleteTitle', { defaultValue: 'Eliminar domicilio' })}
                      Description={t('persons.addresses.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar el domicilio seleccionado?'
                      })}
                      ElementTextHeader={t('persons.addresses.list.street', { defaultValue: 'Calle y número' })}
                      ElementTextInformation={address.street || ''}
                      onConfirmation={() => props.DeleteAddress(address.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.addresses.list.loading', { defaultValue: 'Cargando domicilios...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonAddressFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        addressId={selectedAddressId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonAddressesListComponent as any);
